import { useState } from "react";
import {
  BarChart3,
  Download,
  Globe,
  History,
  Image as ImageIcon,
  RotateCcw,
  Trash2,
  Upload,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader } from "@/components/shared/loader";
import { ObsUploadDialog } from "./ObsUploadDialog";
import { ObsEditChartsDialog } from "./ObsEditChartsDialog";
import { ObsTileDialog } from "./ObsTileDialog";
import { ObsDeleteReportDialog } from "./ObsDeleteReportDialog";
import {
  useActivateObsVersion,
  useUpdateObsReport,
} from "@/features/obs/hooks/useObsQueries";
import { resolveMediaUrl } from "@/features/documents/lib/resolve-media-url";
import { handleApiError } from "@/lib/error-handler";
import type { ObsDatasetVersion, ObsReport, ObsScreen } from "@/types/obs";

const numberFmt = new Intl.NumberFormat("en-US");

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("ar-IQ");
}

/**
 * The stored workbook behind one version.
 *
 * 🔑 A PLAIN LINK TO THE MEDIA FILE, which is what DMS documents already do for a stored file
 * (DocumentsTable) — deliberately NOT `downloadAuthenticatedFile`, which exists for endpoints that
 * GENERATE an export (the backups, reports and CSV exports). There is nothing to generate here:
 * the upload files the workbook in the Media Library as `<slug>-v<n>.xlsx`, so this hands the
 * operator exactly the bytes that produced the live figures. resolveMediaUrl is imported from the
 * documents feature because that is where the shared resolver lives; it is not documents-specific
 * — it turns a local-provider relative path into an absolute one, and leaves an S3/CDN URL alone.
 *
 * ⚠️ RENDERED DISABLED RATHER THAN HIDDEN when a version has no stored file. Two real cases:
 * versions uploaded before the backend started keeping the workbook, and an upload whose media
 * step failed — the backend wraps it in try/catch on purpose, so the figures publish either way.
 * A missing button reads as "this row is somehow different"; a disabled one with a reason reads as
 * what it is.
 */
function DownloadVersionButton({
  version,
  label,
}: {
  version: ObsDatasetVersion;
  label: string;
}) {
  const href = resolveMediaUrl(version.fileUrl ?? undefined);

  if (!href) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled
        title="لا يوجد ملف محفوظ لهذا الإصدار"
      >
        <Download className="size-4" />
        {label}
      </Button>
    );
  }

  return (
    <Button size="sm" variant="outline" asChild>
      {/*
        target="_blank" + rel, exactly as the documents table does it: the file lives on the media
        CDN, so `download` would be ignored cross-origin anyway. An .xlsx is not something a
        browser renders, so it lands in the downloads folder rather than a blank tab.
      */}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={version.sourceFilename ?? label}
      >
        <Download className="size-4" />
        {label}
      </a>
    </Button>
  );
}

/** One of the six screens: what is live, how to replace it, and how to go back. */
export function ObsScreenCard({
  screen,
  report,
}: {
  screen: ObsScreen;
  /**
   * The report's identity row. Present once it has been created or seeded — which
   * is what the tile, chart and delete actions hang off, the six included.
   */
  report?: ObsReport;
}) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [tileOpen, setTileOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  /**
   * Editing charts and deleting are gated on HAVING an identity row, not on
   * whether that row is one of the declared six. The tile always was.
   *
   * ⚠️ BOTH MEAN SOMETHING DIFFERENT FOR THE SIX, and the dialogs say so rather
   * than leaving it to be discovered. Saving charts moves a bespoke screen onto
   * the composed renderer — its KPI row becomes the generic four and its table
   * groups by the chosen dimension — whereas until then the screen is untouched.
   * Deleting removes the identity and the uploaded data but NOT the parsing
   * schema, which lives in obs-schema.json, so one of the six comes back with
   * seed-obs-reports.js and a re-upload. A created report does not.
   */
  const canPickCharts = Boolean(report);
  const activate = useActivateObsVersion();
  const updateReport = useUpdateObsReport();

  /**
   * A report that exists but is not on the site yet.
   *
   * The wizard creates the report at the units step, so abandoning it afterwards
   * and choosing "keep as a draft" leaves exactly this: real, complete, and
   * invisible. Without somewhere to finish it, "finish later" was an instruction
   * with no button behind it — the operator would have had to delete it and walk
   * the whole wizard again.
   *
   * It also covers a report that was deliberately unpublished, which is the
   * reversible alternative the delete dialog points at.
   */
  const isDraft = Boolean(report && !report.isPublished);

  /**
   * No local "published" flag: the mutation invalidates the report queries, the
   * card re-renders from the server's answer, and the button stops being rendered
   * because `isDraft` is false. State that would have to be kept in step with the
   * server is state that eventually is not.
   */
  const publish = async () => {
    if (!report) return;
    try {
      await updateReport.mutateAsync({
        slug: report.slug,
        patch: { isPublished: true },
      });
    } catch (e) {
      handleApiError(e);
    }
  };

  const active = screen.active;
  const history = screen.versions.filter((v) => !v.isActive);

  const rollback = async (version: number) => {
    try {
      await activate.mutateAsync({ slug: screen.slug, version });
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base">{screen.title.ar}</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground" dir="ltr">
              /{screen.slug}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {active ? (
              <Badge variant="secondary">الإصدار {active.version}</Badge>
            ) : (
              /*
                Not an error state. Before the first upload the public site
                serves the fixture committed in the frontend repo, so the screen
                is live and correct — it is simply not being fed from here yet.
              */
              <Badge variant="outline">البيانات الأصلية</Badge>
            )}
            {isDraft && (
              <Badge className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400">
                مسودة — غير منشور
              </Badge>
            )}
            {isDraft && (
              /*
                The one primary action on a draft card, so it reads as the thing
                to do next rather than another option among five outlined ones.
                It stops rendering the moment the server says the report is
                published — see `isDraft`.
              */
              <Button
                size="sm"
                onClick={publish}
                disabled={updateReport.isPending}
              >
                {updateReport.isPending ? (
                  <Loader />
                ) : (
                  <Globe className="size-4" />
                )}
                نشر على الموقع
              </Button>
            )}
            {report && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setTileOpen(true)}
              >
                <ImageIcon className="size-4" />
                {report.tile ? "تغيير البطاقة" : "صورة البطاقة"}
              </Button>
            )}
            {/*
              The LIVE version's workbook. Conditional on `active` rather than on `report`: before
              the first upload the badge beside it reads «البيانات الأصلية» and the site is served
              by the fixture committed in the frontend repo — there is no uploaded file to hand
              over, and offering one would be a lie rather than a disabled button.
            */}
            {active && (
              <DownloadVersionButton version={active} label="تنزيل الملف" />
            )}
            {canPickCharts && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-600"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="size-4" />
                حذف
              </Button>
            )}
            {canPickCharts && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setChartsOpen(true)}
              >
                <BarChart3 className="size-4" />
                الرسوم
              </Button>
            )}
            <Button size="sm" onClick={() => setUploadOpen(true)}>
              <Upload className="size-4" />
              رفع ملف
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {active?.stats ? (
          /*
            `<bdi>` rather than dir="ltr" — see the note in ObsValidationReport:
            dir on the block also left-ALIGNS it, which parts every figure from
            its own right-aligned label.
          */
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">السجلات</div>
              <div className="break-all tabular-nums">
                <bdi>{numberFmt.format(active.stats.rows)}</bdi>
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">السنوات</div>
              <div className="break-all tabular-nums">
                <bdi>
                  {active.stats.years.length
                    ? `${active.stats.years[0]}–${active.stats.years[active.stats.years.length - 1]}`
                    : "—"}
                </bdi>
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">الجهات</div>
              <div className="break-all tabular-nums">
                <bdi>{numberFmt.format(active.stats.entities)}</bdi>
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">آخر نشر</div>
              <div className="break-words">
                {formatDate(active.activatedAt)}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            لم يُرفع أي ملف لهذه الشاشة بعد. الموقع يعرض البيانات الأصلية
            المرفقة مع النظام.
          </p>
        )}

        {history.length > 0 && (
          <div className="border-t border-border pt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHistoryOpen((v) => !v)}
            >
              <History className="size-4" />
              الإصدارات السابقة ({history.length})
            </Button>

            {historyOpen && (
              <ul className="mt-2 space-y-1">
                {history.map((v) => (
                  <li
                    key={v.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0">
                      الإصدار {v.version}
                      <span className="text-muted-foreground">
                        {" "}
                        · {formatDate(v.createdAt)}
                        {v.stats
                          ? ` · ${numberFmt.format(v.stats.rows)} سجل`
                          : ""}
                      </span>
                    </span>
                    <div className="flex items-center gap-2">
                      {/* Short label here: the row already says which version it is. */}
                      <DownloadVersionButton version={v} label="تنزيل" />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={activate.isPending}
                        onClick={() => rollback(v.version)}
                      >
                        {activate.isPending ? (
                          <Loader />
                        ) : (
                          <RotateCcw className="size-4" />
                        )}
                        الرجوع إليه
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>

      {report && (
        <ObsTileDialog
          report={report}
          open={tileOpen}
          onOpenChange={setTileOpen}
        />
      )}

      {report && canPickCharts && (
        <ObsDeleteReportDialog
          report={report}
          versions={screen.versions.length}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
        />
      )}

      {report && canPickCharts && (
        <ObsEditChartsDialog
          report={report}
          open={chartsOpen}
          onOpenChange={setChartsOpen}
        />
      )}

      <ObsUploadDialog
        screen={screen}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
      />
    </Card>
  );
}
